// Vassar College: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Vassar's Common Data Set 2025-2026 (Vassar_College_CDS_2025-2026.pdf,
//    linked from Institutional Research's Common Data Set 2025/26 page): C1
//    applicants/admits/enrollees for Fall 2025 with the residency table
//    (in-state means New York); C8 test policy for Fall 2027; C16 and C21
//    decision dates and early decision counts. Quirk: C21's count block is
//    still headed "For the Fall 2024 entering class", but the 2024-2025 CDS
//    gives different counts (999 applied, 312 admitted), so these (1,046 and
//    402) are Fall 2025. The checkboxes read correctly only in the rendered
//    PDF. G1 matches the cost page below; G5 leaves residents' transportation
//    and other expenses blank, so the cost table uses the admission office's
//    figures.
//  - vassar.edu Admission: First-Year Applicants (deadlines, checklist,
//    optional testing), Music, Art, and Dance Portfolios, and Tuition & Fees
//    (2026-2027 overview). The deadline list carries no year; it is the page
//    as posted on 2026-10-01, with the application open. Vassar's admission
//    pages give no decision dates, so the dates in the deadlines note are the
//    CDS's.
//  - The Early Decision Agreement form (apply.vassar.edu), for what the
//    binding commitment means.
//  - College Scorecard, school id 197133 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Vassar Common Data Set 2025-2026',
        url: 'https://offices.vassar.edu/institutional-research/data/common-data-set-2025-26/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Vassar Admission, First-Year Applicants',
        url: 'https://www.vassar.edu/admission/apply/requirements/',
        read: '2026-10-01',
    },
    arts: {
        label: 'Vassar Admission, Music, Art, and Dance Portfolios',
        url: 'https://www.vassar.edu/admission/apply/requirements/arts/',
        read: '2026-10-01',
    },
    edAgreement: {
        label: 'Vassar College, Early Decision Agreement',
        url: 'https://apply.vassar.edu/apply/earlydecisionagreement.pdf',
        read: '2026-10-01',
    },
    costs: {
        label: 'Vassar Admission, Tuition & Fees',
        url: 'https://www.vassar.edu/admission/financial-aid/tuition/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?197133-Vassar-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'vassar-college',
    kbId: 'vassar_college',
    name: 'Vassar College',
    shortName: 'Vassar',
    location: 'Poughkeepsie, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Vassar style guide colors); decoration only.
    branding: {
        primary: '#951829',
        secondary: '#641A2B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/vassar_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 11118,
        admitted: 2327,
        enrolled: 697,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2194, admitted: 616 },
            { group: 'Out-of-state', applied: 5219, admitted: 1526 },
            { group: 'International', applied: 3705, admitted: 185 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Vassar\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II', applied: 1046, admitted: 402 },
        ],
        note: 'The Common Data Set reports both early decision rounds together, for the fall 2025 entering class. They are part of the totals above. Vassar has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Everyone applies to Vassar the same way. One option is open to artists:',
        rows: [
            { school: 'Music, art and dance (optional)', requirement: 'If you have a well-developed talent in music, art or dance and plan to keep at it at Vassar, you may send a portfolio through SlideRoom; faculty in the discipline evaluate it and share their view with the Office of Admission.' },
        ],
        note: 'Portfolios are due November 17 for Early Decision I and January 5 for Early Decision II and Regular Decision.',
        source: 'arts',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: null, binding: true },
            { plan: 'Early Decision II', deadline: 'January 1', decision: null, binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: null, binding: false },
        ],
        note: 'The application fee is $65, or a fee waiver. Early decision applicants also send the Early Decision Agreement. Vassar\'s admission pages do not publish decision dates; its Common Data Set 2025-2026 lists December 15 for Early Decision I, February 1 for Early Decision II and April 1 for Regular Decision.',
        source: 'firstYear',
        binding: 'Both early decision rounds are binding. You commit to Vassar when you apply and, if admitted, agree to enroll and promptly withdraw your other applications. You may apply to other colleges meanwhile but may have only one early decision application pending at a time. If you apply for financial aid and the award does not make attendance possible, you may decline and be released from the commitment.',
        bindingSource: 'edAgreement',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Vassar does not require SAT or ACT scores. You choose whether to include testing by completing the test optional form in the applicant status portal: by November 22 for Early Decision I, or January 8 for Regular Decision and Early Decision II. Vassar accepts scores you list yourself and superscores; if you enroll, official score reports are required in the spring.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 76140 },
            { item: 'Fees', amount: 1020 },
            { item: 'Food and housing (on campus)', amount: 19800 },
            { item: 'Books, supplies, personal expenses and transportation', amount: 2250 },
        ],
        total: 99210,
        note: 'Tuition, fees and housing and dining are Vassar\'s 2026-2027 charges; the last line is the amount Vassar says to budget.',
        source: 'costs',
        netPrice: {
            average: 36924,
            byIncome: [
                { income: '$0 to $30,000', amount: 19674 },
                { income: '$30,001 to $48,000', amount: 10022 },
                { income: '$48,001 to $75,000', amount: 13428 },
                { income: '$75,001 to $110,000', amount: 17576 },
                { income: 'Over $110,000', amount: 50834 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Vassar\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
